import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Shield, TrendingUp, Users, Wallet, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

// Official Isprava Wealth Logo
const ISPRAVA_LOGO = "https://customer-assets.emergentagent.com/job_ai-finance-chat-1/artifacts/4qsgtdab_Isprava%20Logo%20final.JPG";

const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const [loginData, setLoginData] = useState({ email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await login(loginData.email, loginData.password);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#020617] relative overflow-hidden">
        <div className="absolute inset-0 spotlight" />
        <div className="absolute inset-0 noise-overlay opacity-5" />
        
        <div className="relative z-10 flex flex-col justify-center px-16">
          <div className="mb-12">
            <div className="mb-6">
              <img 
                src={ISPRAVA_LOGO} 
                alt="Isprava Wealth" 
                className="h-auto"
                style={{ maxWidth: '260px', maxHeight: '110px', objectFit: 'contain' }}
                data-testid="login-logo"
              />
            </div>
            <h1 className="font-heading text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
              The Gilded Vault
            </h1>
            <p className="text-lg text-slate-400 max-w-md">
              Manage clients, track performance, and grow your wealth management business.
            </p>
          </div>
          
          <div className="grid grid-cols-2 gap-6 max-w-md">
            <div className="card-vault p-4 rounded-lg">
              <Users className="w-8 h-8 text-[#C5A059] mb-2" />
              <h3 className="font-heading text-white font-semibold">Client Management</h3>
              <p className="text-sm text-slate-400">Track KYC, segment clients</p>
            </div>
            <div className="card-vault p-4 rounded-lg">
              <TrendingUp className="w-8 h-8 text-[#C5A059] mb-2" />
              <h3 className="font-heading text-white font-semibold">Analytics</h3>
              <p className="text-sm text-slate-400">AI-powered insights</p>
            </div>
            <div className="card-vault p-4 rounded-lg">
              <Shield className="w-8 h-8 text-[#C5A059] mb-2" />
              <h3 className="font-heading text-white font-semibold">SEBI Compliant</h3>
              <p className="text-sm text-slate-400">Regulatory adherence</p>
            </div>
            <div className="card-vault p-4 rounded-lg">
              <Wallet className="w-8 h-8 text-[#C5A059] mb-2" />
              <h3 className="font-heading text-white font-semibold">AUM Tracking</h3>
              <p className="text-sm text-slate-400">Portfolio oversight</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Right side - Auth forms */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#0F172A]">
        <div className="w-full max-w-md">
          {/* Mobile branding */}
          <div className="lg:hidden mb-8 text-center">
            <div className="flex items-center justify-center mb-4">
              <img 
                src={ISPRAVA_LOGO} 
                alt="Isprava Wealth" 
                className="h-12 w-auto object-contain rounded"
              />
            </div>
          </div>
          
          <Card className="bg-[#0F172A]/50 border-white/5 backdrop-blur">
            <CardHeader className="text-center">
              <CardTitle className="font-heading text-2xl text-white">Welcome</CardTitle>
              <CardDescription className="text-slate-400">
                Sign In
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="login-email" className="text-slate-300">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="you@example.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    className="input-vault"
                    data-testid="login-email-input"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="login-password" className="text-slate-300">Password</Label>
                    <button
                      type="button"
                      onClick={() => setShowForgotPassword(true)}
                      className="text-xs text-[#C5A059] hover:text-[#D4AF37] transition-colors flex items-center gap-1"
                      data-testid="forgot-password-link"
                    >
                      <KeyRound className="w-3 h-3" />
                      Forgot Password?
                    </button>
                  </div>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    className="input-vault"
                    data-testid="login-password-input"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-gold"
                  disabled={isLoading}
                  data-testid="login-submit-btn"
                >
                  {isLoading ? 'Signing in...' : 'Sign In'}
                </Button>
              </form>
            </CardContent>
          </Card>
          
          <p className="mt-6 text-center text-sm text-slate-500">
            Regulated by SEBI • Secure & Compliant
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onProceedToReset={(token) => {
          navigate(`/reset-password${token ? `?token=${token}` : ''}`);
        }}
      />
    </div>
  );
};

export default LoginPage;
